import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { djs } from '@duke-hq/djs';
import { pdfColors, sharedStyles, getOutcomeBadgeStyle } from './sharedStyles';
import type { AssessmentReportSubmission } from './types';

interface SummaryProps {
  submissions: AssessmentReportSubmission[];
}

const styles = StyleSheet.create({
  section: {
    marginTop: 16,
    padding: 16,
    backgroundColor: pdfColors.background,
    borderWidth: 1.5,
    borderColor: pdfColors.brand,
    borderRadius: 8,
  },
  title: {
    fontSize: 14,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.brand,
    marginBottom: 12,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  label: {
    fontSize: 11,
    width: 200,
    color: pdfColors.textMuted,
  },
  value: {
    fontSize: 11,
    flex: 1,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.text,
  },
});

function Summary({ submissions }: SummaryProps): React.ReactElement | null {
  if (submissions.length === 0) {
    return null;
  }

  const lastSubmission = submissions[submissions.length - 1];
  const firstSubmission = submissions[0];
  const finalOutcome = lastSubmission?.outcome ?? '';

  return (
    <View style={styles.section} wrap={false}>
      <Text style={styles.title}>Assessment Summary</Text>

      <View style={styles.row}>
        <Text style={styles.label}>Total Submissions:</Text>
        <Text style={styles.value}>{submissions.length}</Text>
      </View>

      <View style={styles.row}>
        <Text style={styles.label}>Final Outcome:</Text>
        <View style={[sharedStyles.badgeBase, getOutcomeBadgeStyle(finalOutcome)]}>
          <Text style={sharedStyles.badgeText}>
            {finalOutcome.toUpperCase().replace(/_/g, ' ')}
          </Text>
        </View>
      </View>

      {finalOutcome.toUpperCase() === 'SATISFACTORY' && lastSubmission?.assessedAt != null ? (
        <View style={styles.row}>
          <Text style={styles.label}>Date of Completion:</Text>
          <Text style={styles.value}>{djs(lastSubmission.assessedAt).format('LLL')}</Text>
        </View>
      ) : null}

      {submissions.length > 1 ? (
        <View style={styles.row}>
          <Text style={styles.label}>Duration (First to Final):</Text>
          {(() => {
            const days = djs(lastSubmission?.submittedAt).diff(
              djs(firstSubmission?.submittedAt),
              'day',
            );
            return (
              <Text style={styles.value}>
                {days === 0 ? 'Less than 1 day' : `${days} day${days === 1 ? '' : 's'}`}
              </Text>
            );
          })()}
        </View>
      ) : null}
    </View>
  );
}

export default Summary;
